import React from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";

const Support: React.FC = () => {
  const { isDarkMode } = useTheme();

  const supportOptions = [
    {
      title: "Contact Us",
      description: "Get in touch with our support team for personalized assistance",
      icon: "📞",
      link: `/${window.location.pathname.split('/')[1]}/contact`
    },
    {
      title: "FAQ",
      description: "Find answers to frequently asked questions",
      icon: "❓",
      link: `/${window.location.pathname.split('/')[1]}/faq`
    },
    {
      title: "Account Help",
      description: "Issues with login, profile, or account management",
      icon: "👤",
      link: `/${window.location.pathname.split('/')[1]}/contact`
    },
    {
      title: "Program Registration",
      description: "Help with enrolling in programs and managing registrations",
      icon: "📝",
      link: `/${window.location.pathname.split('/')[1]}/contact`
    }
  ];

  const commonIssues = [
    {
      issue: "Forgot Password",
      solution: "Use the 'Forgot Password' link on the login page to reset your password."
    },
    {
      issue: "Payment Issues",
      solution: "Contact our support team immediately if you experience payment problems or need refund information."
    },
    {
      issue: "Program Changes",
      solution: "Contact us as soon as possible to request changes to your program enrollment."
    },
    {
      issue: "Technical Problems",
      solution: "Try refreshing the page or clearing your browser cache. If issues persist, contact our support team."
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Navbar />

      <div className="flex-1 py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl 3xl:max-w-7xl 4xl:max-w-screen-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Support Center
            </h1>
            <p className={`text-base sm:text-lg 3xl:text-xl 4xl:text-2xl leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              We're here to help! Find answers to your questions or get in touch with our support team.
            </p>
          </div>

          {/* Support Options Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {supportOptions.map((option, index) => (
              <a
                key={index}
                href={option.link}
                className={`p-6 sm:p-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-center group ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              >
                <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">
                  {option.icon}
                </div>
                <h3 className={`text-lg sm:text-xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'} group-hover:opacity-80`}>
                  {option.title}
                </h3>
                <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {option.description}
                </p>
              </a>
            ))}
          </div>

          {/* Common Issues Section */}
          <div className={`rounded-lg shadow-md p-6 sm:p-8 mb-8 sm:mb-12 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Common Issues & Quick Solutions
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {commonIssues.map((item, index) => (
                <div key={index} className={`border-l-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} pl-4 sm:pl-6`}>
                  <h3 className={`text-lg sm:text-xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.issue}
                  </h3>
                  <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {item.solution}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Support Section */}
          <div className={`rounded-lg shadow-md p-6 sm:p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center">
              <h2 className={`text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Still Need Help?
              </h2>
              <p className={`text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Our support team is standing by to assist you with any questions or concerns.
              </p>

              <div className="flex flex-col sm:flex-row 3xl:flex-row gap-4 3xl:gap-6 4xl:gap-8 justify-center items-center max-w-lg 3xl:max-w-xl 4xl:max-w-2xl mx-auto">
                <a
                  href={`/${window.location.pathname.split('/')[1]}/contact`}
                  className="w-full sm:w-auto inline-block px-6 py-3 sm:px-8 sm:py-4 bg-[var(--cc-action)] text-[var(--cc-action-text)] font-semibold rounded-lg hover:bg-[var(--cc-action-hover)] transition-colors duration-200 text-sm sm:text-base"
                >
                  Contact Support
                </a>

                <a
                  href={`/${window.location.pathname.split('/')[1]}/faq`}
                  className={`w-full sm:w-auto inline-block px-6 py-3 sm:px-8 sm:py-4 border-2 font-semibold rounded-lg transition-colors ${isDarkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} duration-200 text-sm sm:text-base`}
                >
                  View FAQ
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Support;
